/** History filters: by status and by company/house. Both apply together. */
import type { Entry } from '../api/types.ts';

export const STATUS_FILTERS = ['all', 'pending', 'approved', 'needs_correction', 'rejected', 'unsent'] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** 'all' or an entity id. */
export type PlaceFilter = string;

export function isStatusFilter(v: string | null): v is StatusFilter {
  return v !== null && (STATUS_FILTERS as readonly string[]).includes(v);
}

/** Entries waiting on the phone ("Not sent") are kept apart from the server's statuses. */
export function matchesStatus(e: Entry, f: StatusFilter): boolean {
  const unsent = e.syncState !== 'synced';
  if (f === 'all') return true;
  if (f === 'unsent') return unsent;
  return !unsent && e.status === f;
}

export const matchesPlace = (e: Entry, place: PlaceFilter) => place === 'all' || e.entityId === place;

export function applyFilters(entries: Entry[], status: StatusFilter, place: PlaceFilter): Entry[] {
  return entries.filter((e) => matchesStatus(e, status) && matchesPlace(e, place));
}

/** Count on each status chip, within the company/house picked. */
export function statusCounts(entries: Entry[], place: PlaceFilter): Record<StatusFilter, number> {
  const here = entries.filter((e) => matchesPlace(e, place));
  return Object.fromEntries(STATUS_FILTERS.map((s) => [s, here.filter((e) => matchesStatus(e, s)).length])) as Record<StatusFilter, number>;
}

/** Count on each company chip, within the status picked. */
export function placeCounts(entries: Entry[], status: StatusFilter, places: string[]): Record<string, number> {
  const these = entries.filter((e) => matchesStatus(e, status));
  return Object.fromEntries([['all', these.length], ...places.map((id) => [id, these.filter((e) => e.entityId === id).length])]);
}
